import { differenceInCalendarDays, isSameDay } from 'date-fns';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { parseUseDayBookings } from '@shared/functions/parse-use-day-bookings';

// KEEP IN SYNC mit up2boat-ym-api/app/bcm/@helpers/get-duration-from-dates.ts: gleiche Logik, da FE
// und BE kein gemeinsames Package teilen. Import-Pfad/Kommentare weichen bewusst ab — bei jeder
// Logikänderung beide Dateien anpassen.

export enum DurationUnit {
    Days = 'Days',
    Overnights = 'Overnights',
}

export function getDurationFromDates(fromDate: Date, toDate: Date, durationUnit: DurationUnit, defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit): number {

    if (!fromDate || !toDate) {
        return null;
    }

    // Rückwärts/ungültiger Zeitraum → keine sinnvolle Dauer (wie bei fehlenden Daten), statt einer
    // negativen Position oder einer stillen 1-Einheit-Buchung.
    if (differenceInCalendarDays(toDate, fromDate) < 0) {
        return null;
    }

    switch (durationUnit) {

        case DurationUnit.Overnights: {

            // Mehrtägig: Nächte = reines Kalendertag-Delta. Die +1-Korrekturen unten gelten nur für einen
            // Same-Day-Tagesgast außerhalb des Fensters (vgl. _calculateDurationOfStay), sonst Overcount.
            if (!isSameDay(fromDate, toDate)) {
                return differenceInCalendarDays(toDate, fromDate);
            }

            // Same calendar day with day bookings enabled = one overnight (untertägige Belegung).
            if (parseUseDayBookings(defaultBerthReservationTimeUnit?.useDayBookings)) {
                return 1;
            }

            // Settings arrive as varchar ('00', '08'); without Number() '00' === 0 is false (#6981).
            const fromHours = Number(defaultBerthReservationTimeUnit?.dailyGuestFromHours) || 0;
            const fromMinutes = Number(defaultBerthReservationTimeUnit?.dailyGuestFromMinutes) || 0;
            const toHours = Number(defaultBerthReservationTimeUnit?.dailyGuestToHours) || 0;
            const toMinutes = Number(defaultBerthReservationTimeUnit?.dailyGuestToMinutes) || 0;

            const isAfterPeriodStarts = fromHours < fromDate.getHours() ||
                (fromHours === fromDate.getHours() && fromMinutes <= fromDate.getMinutes());

            const isAfterPeriodEnds = toHours < toDate.getHours() ||
                (toHours === toDate.getHours() && toMinutes < toDate.getMinutes());

            let durationOfStay = 1;

            if (!isAfterPeriodStarts) {
                durationOfStay++;
            }

            if (isAfterPeriodEnds) {
                durationOfStay++;
            }

            return durationOfStay;
        }

        case DurationUnit.Days:
            return differenceInCalendarDays(toDate, fromDate) + 1;

        default: {
            // Erzwingt zur Compile-Zeit, dass jeder DurationUnit-Wert behandelt wird (kein stiller NaN).
            const _exhaustive: never = durationUnit;
            throw new Error(`Unbekannte DurationUnit: ${_exhaustive}`);
        }
    }

}
